import React, { useEffect, useState } from 'react';
import Select from 'modules/views/shared/Select';
import { Box, CircularProgress } from '@mui/material';
import { Document, Page } from 'react-pdf';
import { useAppDispatch } from 'modules/state-mgmt/store';
import { fetchBadgePdf } from 'modules/state-mgmt/cache';
import { enqueueSnackbar } from 'notistack';
import { prettifyErrorResponse } from 'utils';
import { useTranslation } from 'react-i18next';
import Modal from 'modules/views/shared/Modal';
import useStyles from './EBadgeModalStyle';
import { Project } from '../types';

// pdf.js's official webpack integration: it bundles the pdf.js worker as its own webpack chunk,
// served from our own origin, and registers it with the pdfjs-dist instance react-pdf uses.
// Previously the worker was fetched from unpkg.com at runtime, which the deployed site's
// Content-Security-Policy (script-src) blocks, so the PDF downloaded fine but couldn't be drawn
// ("Failed to load PDF file."). Keeping it same-origin also removes any CDN outage risk.
// Don't swap this for `workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url)`:
// CRA runs node_modules through Babel and emits that file unbundled, so in production builds its
// injected helper imports are left unresolved and the worker fails to load.
import 'pdfjs-dist/webpack.mjs';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

interface EBadgeModalProps {
  isVisible: boolean;
  onClose: () => void;
  selectedProject: Project;
  isWorker: boolean;
}

const EBadgeModal: React.FC<EBadgeModalProps> = ({ isVisible, onClose, selectedProject, isWorker }) => {
  const classes = useStyles();
  const [pdfString, setPdfString] = useState<string | null>(null);
  const [selectCompany, setSelectedCompany] = useState<{ label: string; value: string } | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  useEffect(() => {
    if (isVisible && selectedProject?.companies?.length) {
      if (selectedProject.companies.length === 1) {
        const company = selectedProject.companies[0];
        setSelectedCompany({
          label: company?.company?.name || '',
          value: company?.company?.id || '',
        });
      } else {
        setPdfString(null);
        setSelectedCompany(null);
      }
    } else {
      setPdfString(null);
      setSelectedCompany(null);
    }
  }, [isVisible, selectedProject]);

  const onViewEBadge = async (badgeId: string, templateId?: string) => {
    setIsLoading(true);
    try {
      const badgePdf = await dispatch(
        fetchBadgePdf({
          id: badgeId,
          languageCode: 'en',
          templateId: templateId,
          isWorker: isWorker,
        }),
      ).unwrap();
      const badgePdfString = typeof badgePdf === 'string' ? badgePdf : '';
      const base64Data = badgePdfString?.startsWith('data:') ? badgePdfString?.split(',')[1] : badgePdfString;
      setPdfString(base64Data);
    } catch (error) {
      enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectCompany) {
      const company = selectedProject?.companies?.find(comp => comp?.company?.id === selectCompany?.value);
      if (company?.badgeId) {
        onViewEBadge(company.badgeId, company.projectBadgeTemplateId);
      }
    }
  }, [selectCompany]);

  if (!isVisible) {
    return null;
  }

  return (
    <Modal
      isVisible={isVisible}
      title={t('projectList.eBadgeModal.title')}
      modalStyleClass={classes.modal}
      modalContentStyleClass={classes.modalContent}
      testId="ebadge-modal"
      Content={
        <div>
          <Select
            options={selectedProject?.companies?.map(company => ({
              label: company?.company?.name || '',
              value: company?.company?.id || '',
            }))}
            selectedValue={selectCompany}
            testId="ebadge-company-select"
            onChange={value => setSelectedCompany(value)}
            inputLabel={t('projectList.eBadgeModal.selectCompany')}
          />
          {isLoading ? (
            <div className={classes.loadingContainer} data-testid="ebadge-loader">
              <CircularProgress />
            </div>
          ) : pdfString ? (
            <div data-testid="ebadge-pdf">
              <Document
                className={classes.pdfContainer}
                file={`data:application/pdf;base64,${pdfString}`}
                loading={
                  <div className={classes.loadingContainer}>
                    <CircularProgress />
                  </div>
                }
              >
                <Page height={435} width={303} className={classes.pdfPage} pageNumber={1} />
                <div className={classes.pdfMargin} />
                <Page height={435} width={303} className={classes.pdfPage} pageNumber={2} />
              </Document>
            </div>
          ) : (
            <Box className={classes.noDataText} data-testid="ebadge-empty-state">
              {!selectCompany ? t('projectList.eBadgeModal.placeholder') : t('projectList.eBadgeModal.noData')}
            </Box>
          )}
        </div>
      }
      onClose={onClose}
    />
  );
};

export default EBadgeModal;
